import { supabase } from '@/integrations/supabase/client';
import type {
  Notification,
  NotificationAction,
  NotificationMedia,
  NotificationPriority,
  NotificationType,
  CreateNotificationPayload,
  INotificationBuilder,
  SYSTEM_CHANNELS,
} from '@/types/notifications';

// ============================================================================
// CHANNEL CACHE
// ============================================================================

interface ChannelCacheEntry {
  id: string;
  sound: string;
  importance: string;
  cachedAt: number;
}

// In-memory cache for channel lookups (5 minute TTL)
const channelCache = new Map<string, ChannelCacheEntry>();
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

// Clear expired entries periodically
if (typeof window !== 'undefined') {
  setInterval(() => {
    const now = Date.now();
    for (const [key, entry] of channelCache.entries()) {
      if (now - entry.cachedAt > CACHE_TTL_MS) {
        channelCache.delete(key);
      }
    }
  }, 60 * 1000); // Check every minute
}

// ============================================================================
// NOTIFICATION BUILDER CLASS
// ============================================================================

/**
 * Fluent API for building and sending notifications
 * 
 * @example
 * ```typescript
 * await new NotificationBuilder()
 *   .setTitle("New Message")
 *   .setBody("You have a message from Dr. Smith")
 *   .setChannel("messages")
 *   .setPriority("high")
 *   .addAction("reply", "Reply")
 *   .send();
 * ```
 */
export class NotificationBuilder implements INotificationBuilder {
  private payload: Partial<CreateNotificationPayload> = {
    actions: [],
  };

  constructor(private userId?: string, private organizationId?: string) {
    if (userId) this.payload.userId = userId;
    if (organizationId) this.payload.organizationId = organizationId;
  }

  // ============================================================================
  // CONTENT METHODS
  // ============================================================================

  setTitle(title: string): this {
    this.payload.title = title;
    return this;
  }

  setBody(body: string): this {
    this.payload.body = body;
    return this;
  }

  setCategory(category: string): this {
    this.payload.category = category;
    return this;
  }

  setLinkUrl(url: string): this {
    this.payload.linkUrl = url;
    return this;
  }

  // ============================================================================
  // CLASSIFICATION METHODS
  // ============================================================================

  setChannel(channelNameOrId: string): this {
    // Check if it's a UUID (channel ID) or a name
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    
    if (uuidRegex.test(channelNameOrId)) {
      this.payload.channelId = channelNameOrId;
    } else {
      this.payload.channel = channelNameOrId;
    }
    
    return this;
  }

  setPriority(priority: NotificationPriority): this {
    this.payload.priority = priority;
    return this;
  }

  setType(type: NotificationType): this {
    this.payload.notificationType = type;
    return this;
  }

  // ============================================================================
  // APP IDENTIFICATION METHODS
  // ============================================================================

  setApp(appId: string, appName: string, appIcon?: string): this {
    this.payload.appId = appId;
    this.payload.appName = appName;
    if (appIcon) this.payload.appIcon = appIcon;
    return this;
  }

  // ============================================================================
  // GROUPING METHODS
  // ============================================================================

  setThread(threadId: string): this {
    this.payload.threadId = threadId;
    return this;
  }

  setGroupKey(groupKey: string): this {
    this.payload.groupKey = groupKey;
    return this;
  }

  // ============================================================================
  // BEHAVIOR METHODS
  // ============================================================================

  setSound(sound: string): this {
    this.payload.sound = sound;
    return this;
  }

  requiresInteraction(required: boolean): this {
    this.payload.requiresInteraction = required;
    return this;
  }

  setAutoDismiss(seconds: number): this {
    this.payload.autoDismissAfter = seconds;
    return this;
  }

  // ============================================================================
  // ACTION METHODS
  // ============================================================================

  addAction(id: string, label: string, options?: Partial<NotificationAction>): this {
    const action: NotificationAction = {
      id,
      label,
      type: options?.type || 'button',
      variant: options?.variant || 'default',
      icon: options?.icon,
      handler: options?.handler,
      data: options?.data,
      placeholder: options?.placeholder,
      url: options?.url,
    };

    if (!this.payload.actions) {
      this.payload.actions = [];
    }
    
    this.payload.actions.push(action);
    return this;
  }

  clearActions(): this {
    this.payload.actions = [];
    return this;
  }

  // ============================================================================
  // MEDIA METHODS
  // ============================================================================

  setMedia(media: NotificationMedia): this {
    this.payload.media = media;
    return this;
  }

  setAvatar(url: string): this {
    this.payload.avatarUrl = url;
    return this;
  }

  // ============================================================================
  // SCHEDULING METHODS
  // ============================================================================

  schedule(date: Date | string): this {
    this.payload.scheduledFor = date;
    return this;
  }

  setExpiration(date: Date | string): this {
    this.payload.expiresAt = date;
    return this;
  }

  // ============================================================================
  // METADATA METHODS
  // ============================================================================

  setMetadata(data: Record<string, unknown>): this {
    this.payload.metadata = data;
    return this;
  }

  // ============================================================================
  // VALIDATION & EXECUTION
  // ============================================================================

  validate(): boolean {
    if (!this.payload.title) {
      console.error('NotificationBuilder: Title is required');
      return false;
    }

    if (!this.payload.userId) {
      console.error('NotificationBuilder: UserId is required');
      return false;
    }

    if (!this.payload.organizationId) {
      console.error('NotificationBuilder: OrganizationId is required');
      return false;
    }

    return true;
  }

  async send(): Promise<Notification> {
    if (!this.validate()) {
      throw new Error('Notification validation failed');
    }

    try {
      // Resolve channel name to ID and fetch channel defaults
      let channelId = this.payload.channelId;
      let channelDefaults: { sound?: string; importance?: string } = {};
      
      if (this.payload.channel && !channelId) {
        // Check cache first
        const cacheKey = this.payload.channel.toLowerCase();
        const cached = channelCache.get(cacheKey);
        const now = Date.now();
        
        if (cached && (now - cached.cachedAt) < CACHE_TTL_MS) {
          // Use cached data
          channelId = cached.id;
          channelDefaults = {
            sound: cached.sound,
            importance: cached.importance,
          };
        } else {
          // Query database
          const { data: channel } = await supabase
            .from('notification_channels')
            .select('id, sound, importance')
            .ilike('name', this.payload.channel)
            .limit(1)
            .single();
          
          if (channel) {
            channelId = channel.id;
            channelDefaults = {
              sound: channel.sound,
              importance: channel.importance,
            };
            
            // Cache the result
            channelCache.set(cacheKey, {
              id: channel.id,
              sound: channel.sound,
              importance: channel.importance,
              cachedAt: now,
            });
          } else {
            console.warn(`[NotificationBuilder] Channel "${this.payload.channel}" not found`);
          }
        }
      } else if (channelId) {
        // Check cache by ID
        const cached = Array.from(channelCache.values()).find(e => e.id === channelId);
        const now = Date.now();
        
        if (cached && (now - cached.cachedAt) < CACHE_TTL_MS) {
          // Use cached data
          channelDefaults = {
            sound: cached.sound,
            importance: cached.importance,
          };
        } else {
          // Fetch defaults for existing channel ID
          const { data: channel } = await supabase
            .from('notification_channels')
            .select('sound, importance')
            .eq('id', channelId)
            .single();
          
          if (channel) {
            channelDefaults = {
              sound: channel.sound,
              importance: channel.importance,
            };
          }
        }
      }

      // Build the notification record
      const notificationData: Record<string, unknown> = {
        user_id: this.payload.userId!,
        organization_id: this.payload.organizationId,
        title: this.payload.title!,
        body: this.payload.body,
        category: this.payload.category,
        link_url: this.payload.linkUrl,
        
        // Classification
        channel_id: channelId,
        priority: this.payload.priority || channelDefaults.importance || 'default',
        notification_type: this.payload.notificationType || 'system',
        
        // App identification
        app_id: this.payload.appId,
        app_name: this.payload.appName,
        app_icon: this.payload.appIcon,
        
        // Grouping
        thread_id: this.payload.threadId,
        group_key: this.payload.groupKey || this.payload.appName || 'general',
        
        // Behavior
        sound: this.payload.sound || channelDefaults.sound || 'default',
        requires_interaction: this.payload.requiresInteraction || false,
        auto_dismiss_after: this.payload.autoDismissAfter,
        
        // Actions and media
        actions: this.payload.actions || [],
        action_buttons: this.payload.actions || [], // Legacy compatibility
        media: this.payload.media,
        avatar_url: this.payload.avatarUrl,
        
        // Scheduling
        scheduled_for: this.payload.scheduledFor,
        expires_at: this.payload.expiresAt,
        delivered_at: this.payload.scheduledFor ? null : new Date().toISOString(),
        
        // Metadata
        metadata: this.payload.metadata,
        
        // State
        is_read: false,
        is_archived: false,
        status: 'unread',
      };

      // Insert into database
      const { data, error } = await supabase
        .from('notifications')
        .insert(notificationData)
        .select()
        .single();

      if (error) {
        console.error('Error creating notification:', error);
        throw error;
      }

      return data as Notification;
    } catch (error) {
      console.error('NotificationBuilder.send() error:', error);
      throw error;
    }
  }

  // ============================================================================
  // HELPER: Get current payload (for debugging)
  // ============================================================================

  getPayload(): Partial<CreateNotificationPayload> {
    return { ...this.payload };
  }
}

// ============================================================================
// NOTIFICATION TEMPLATES
// ============================================================================

/**
 * Pre-built notification templates for common use cases
 */
export class NotificationTemplates {
  /**
   * Create a mention notification
   * @example
   * ```typescript
   * await NotificationTemplates.mention(
   *   userId,
   *   orgId,
   *   "John Doe",
   *   "avatarUrl",
   *   "Great work on the presentation!",
   *   "project-123"
   * );
   * ```
   */
  static async mention(
    userId: string,
    organizationId: string,
    mentionerName: string,
    mentionerAvatar: string,
    message: string,
    threadId: string
  ): Promise<Notification> {
    return new NotificationBuilder(userId, organizationId)
      .setTitle(`${mentionerName} mentioned you`)
      .setBody(message)
      .setChannel('Messages')
      .setPriority('high')
      .setType('team')
      .setAvatar(mentionerAvatar)
      .setThread(threadId)
      .setGroupKey('mentions')
      .addAction('reply', 'Reply', { type: 'input', placeholder: 'Type your reply...' })
      .addAction('view', 'View Thread', { type: 'link' })
      .send();
  }

  /**
   * Create a join request notification
   */
  static async joinRequest(
    userId: string,
    organizationId: string,
    requesterName: string,
    requesterAvatar: string,
    projectName: string,
    requestId: string
  ): Promise<Notification> {
    return new NotificationBuilder(userId, organizationId)
      .setTitle(`${requesterName} asked to join ${projectName}`)
      .setBody('Review their request and decide whether to approve or decline')
      .setChannel('Team Activity')
      .setPriority('high')
      .setType('team')
      .setAvatar(requesterAvatar)
      .setThread(requestId)
      .setGroupKey('join-requests')
      .addAction('decline', 'Decline', { variant: 'outline', data: { requestId } })
      .addAction('approve', 'Approve', { variant: 'default', data: { requestId } })
      .requiresInteraction(true)
      .send();
  }

  /**
   * Create a file upload notification
   */
  static async fileUpload(
    userId: string,
    organizationId: string,
    uploaderName: string,
    uploaderAvatar: string,
    files: { name: string; size: string }[],
    projectName: string
  ): Promise<Notification> {
    const fileCount = files.length;
    const fileNames = files.map(f => f.name).join(', ');

    return new NotificationBuilder(userId, organizationId)
      .setTitle(`${uploaderName} uploaded ${fileCount} file${fileCount > 1 ? 's' : ''} to ${projectName}`)
      .setBody(fileNames)
      .setChannel('Team Activity')
      .setPriority('default')
      .setType('team')
      .setAvatar(uploaderAvatar)
      .setGroupKey('file-uploads')
      .setMetadata({ files })
      .addAction('download', 'Download All', { icon: 'Download' })
      .addAction('view', 'View Files', { type: 'link' })
      .send();
  }

  /**
   * Create a system alert notification
   */
  static async systemAlert(
    userId: string,
    organizationId: string,
    title: string,
    message: string,
    severity: 'info' | 'warning' | 'error' = 'info'
  ): Promise<Notification> {
    const priorityMap = {
      info: 'default' as NotificationPriority,
      warning: 'high' as NotificationPriority,
      error: 'urgent' as NotificationPriority,
    };

    const iconMap = {
      info: 'Info',
      warning: 'AlertTriangle',
      error: 'AlertCircle',
    };

    return new NotificationBuilder(userId, organizationId)
      .setTitle(title)
      .setBody(message)
      .setChannel('System Updates')
      .setPriority(priorityMap[severity])
      .setType('system')
      .setApp('system', 'System', iconMap[severity])
      .setGroupKey('system-alerts')
      .send();
  }

  /**
   * Create an alarm notification
   */
  static async alarm(
    userId: string,
    organizationId: string,
    title: string,
    time: string
  ): Promise<Notification> {
    return new NotificationBuilder(userId, organizationId)
      .setTitle(title)
      .setBody(`Alarm set for ${time}`)
      .setChannel('Alarms')
      .setPriority('urgent')
      .setType('system')
      .setSound('alarm')
      .requiresInteraction(true)
      .setApp('clock', 'Clock', 'AlarmClock')
      .addAction('snooze', 'Snooze 5m', { data: { duration: 5 } })
      .addAction('dismiss', 'Dismiss', { variant: 'default' })
      .send();
  }

  /**
   * Create a reminder notification
   */
  static async reminder(
    userId: string,
    organizationId: string,
    title: string,
    body: string,
    scheduledFor?: Date | string
  ): Promise<Notification> {
    const builder = new NotificationBuilder(userId, organizationId)
      .setTitle(title)
      .setBody(body)
      .setChannel('Reminders')
      .setPriority('high')
      .setType('system')
      .setSound('reminder')
      .setApp('reminders', 'Reminders', 'BellRing')
      .setGroupKey('reminders')
      .addAction('complete', 'Mark Complete', { variant: 'default' })
      .addAction('snooze', 'Snooze 1h', { variant: 'outline', data: { duration: 60 } });

    if (scheduledFor) {
      builder.schedule(scheduledFor);
    }

    return builder.send();
  }

  /**
   * Create a calendar event notification
   */
  static async calendarEvent(
    userId: string,
    organizationId: string,
    eventTitle: string,
    eventTime: string,
    minutesBefore: number = 10
  ): Promise<Notification> {
    return new NotificationBuilder(userId, organizationId)
      .setTitle(`${eventTitle} starts in ${minutesBefore} minutes`)
      .setBody(`Scheduled for ${eventTime}`)
      .setChannel('Calendar')
      .setPriority('high')
      .setType('app')
      .setApp('calendar', 'Calendar', 'Calendar')
      .setGroupKey('calendar-events')
      .addAction('join', 'Join Now', { variant: 'default' })
      .addAction('snooze', 'Remind in 5m', { variant: 'outline', data: { duration: 5 } })
      .send();
  }

  /**
   * Create a task assignment notification
   */
  static async taskAssignment(
    userId: string,
    organizationId: string,
    taskTitle: string,
    assignedBy: string,
    assignedByAvatar: string,
    dueDate?: string,
    taskId?: string
  ): Promise<Notification> {
    const body = dueDate ? `Due: ${dueDate}` : 'No due date set';

    return new NotificationBuilder(userId, organizationId)
      .setTitle(`${assignedBy} assigned you: ${taskTitle}`)
      .setBody(body)
      .setChannel('Team Activity')
      .setPriority('high')
      .setType('team')
      .setAvatar(assignedByAvatar)
      .setThread(taskId)
      .setGroupKey('task-assignments')
      .addAction('view', 'View Task', { type: 'link', data: { taskId } })
      .addAction('accept', 'Accept', { variant: 'default', data: { taskId } })
      .send();
  }

  /**
   * Create a security alert notification
   */
  static async securityAlert(
    userId: string,
    organizationId: string,
    title: string,
    message: string,
    actionRequired: boolean = false
  ): Promise<Notification> {
    const builder = new NotificationBuilder(userId, organizationId)
      .setTitle(title)
      .setBody(message)
      .setChannel('Security Alerts')
      .setPriority('urgent')
      .setType('system')
      .setApp('security', 'Security', 'ShieldAlert')
      .setGroupKey('security-alerts');

    if (actionRequired) {
      builder
        .requiresInteraction(true)
        .addAction('review', 'Review Now', { variant: 'destructive' })
        .addAction('dismiss', 'Dismiss', { variant: 'outline' });
    }

    return builder.send();
  }

  /**
   * Create a progress notification (e.g., file upload, processing)
   */
  static async progress(
    userId: string,
    organizationId: string,
    title: string,
    body: string,
    percentage: number,
    appName: string = 'System',
    appIcon: string = 'Loader'
  ): Promise<Notification> {
    return new NotificationBuilder(userId, organizationId)
      .setTitle(title)
      .setBody(body)
      .setChannel('System Updates')
      .setPriority('low')
      .setType('app')
      .setApp('progress', appName, appIcon)
      .setMedia({
        type: 'progress',
        value: percentage,
        max: 100,
      })
      .setGroupKey(`${appName}-progress`)
      .setAutoDismiss(5)
      .send();
  }
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Quick helper to send a simple notification
 */
export async function sendNotification(
  userId: string,
  title: string,
  body?: string,
  options?: Partial<CreateNotificationPayload>
): Promise<Notification> {
  const builder = new NotificationBuilder(userId, options?.organizationId)
    .setTitle(title);

  if (body) builder.setBody(body);
  if (options?.channel) builder.setChannel(options.channel);
  if (options?.priority) builder.setPriority(options.priority);
  if (options?.appName) builder.setApp(options.appId || 'app', options.appName, options.appIcon);
  if (options?.actions) {
    options.actions.forEach(action => builder.addAction(action.id, action.label, action));
  }

  return builder.send();
}

/**
 * Quick helper to send a toast-style notification (auto-dismiss)
 */
export async function sendToast(
  userId: string,
  title: string,
  body?: string,
  durationSeconds: number = 5
): Promise<Notification> {
  return new NotificationBuilder(userId)
    .setTitle(title)
    .setBody(body)
    .setPriority('low')
    .setAutoDismiss(durationSeconds)
    .send();
}
